import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, BarChart3, MapPin, Trash2, Globe2 } from "lucide-react";

export default function Volumetria() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [empresas, setEmpresas] = useState([]);
  const [empresaSel, setEmpresaSel] = useState("");
  const [vols, setVols] = useState([]);
  const [agregado, setAgregado] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [catalogo, setCatalogo] = useState({ servicos_por_nicho: {} });
  const [pendentes, setPendentes] = useState(null);

  useEffect(() => {
    api.get("/catalogo/nichos").then(r => setCatalogo(r.data)).catch(() => {});
    api.get("/distribuicao/pendentes").then(r => setPendentes(r.data)).catch(() => {});
    if (isAdmin) {
      api.get("/empresas").then(r => {
        setEmpresas(r.data || []);
        if (r.data?.length) setEmpresaSel(r.data[0].id);
      });
      api.get("/admin/volumetria/agregado").then(r => setAgregado(r.data || [])).catch(() => {});
    } else if (user?.empresa_id) {
      setEmpresaSel(user.empresa_id);
    }
  }, [isAdmin, user]);

  const loadVols = useCallback(() => {
    if (!empresaSel) return;
    api.get(`/empresas/${empresaSel}/volumetria`).then(r => setVols(r.data || [])).catch(() => {});
  }, [empresaSel]);

  useEffect(loadVols, [loadVols]);

  const submit = async () => {
    try {
      await api.post(`/empresas/${empresaSel}/volumetria`, {
        ...form,
        empresa_id: empresaSel,
        media_mensal: parseInt(form.media_mensal, 10) || 0,
      });
      toast.success("Volumetria registrada");
      setOpen(false);
      setForm(emptyForm());
      loadVols();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remover = async (vid) => {
    if (!window.confirm("Remover este registro?")) return;
    try {
      await api.delete(`/empresas/${empresaSel}/volumetria/${vid}`);
      toast.success("Removido");
      loadVols();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const todosServicos = Object.values(catalogo.servicos_por_nicho || {}).flat();
  const servicosUnicos = [...new Set(todosServicos)];

  const totalEmpresa = vols.reduce((acc, v) => acc + (v.media_mensal || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-end gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">MontaJá · Operação</div>
          <h1 className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1">Volumetria por Região</h1>
          <p className="text-sm text-aj-text-muted mt-1">
            Estimativa mensal de atendimentos por cidade/bairro — base para distribuição e capacidade regional.
          </p>
        </div>
        {empresaSel && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button className="btn-aj-primary text-sm" data-testid="btn-nova-volumetria">
                <Plus size={16} /> Nova região
              </button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Nova volumetria</DialogTitle>
                <DialogDescription>Estimativa mensal de atendimentos por região para planejamento de capacidade.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <FF label="Estado" v={form.estado} on={(v) => setForm(f => ({...f, estado: v}))} testid="vol-estado" />
                  <FF label="Cidade" v={form.cidade} on={(v) => setForm(f => ({...f, cidade: v}))} testid="vol-cidade" />
                  <FF label="Bairro/região" v={form.bairro} on={(v) => setForm(f => ({...f, bairro: v}))} testid="vol-bairro" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <FF label="Média mensal" v={form.media_mensal} on={(v) => setForm(f => ({...f, media_mensal: v}))} testid="vol-media" type="number" />
                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Tipo de serviço</Label>
                    <Select value={form.tipo_servico} onValueChange={(v) => setForm(f => ({...f, tipo_servico: v}))}>
                      <SelectTrigger className="mt-2 rounded-lg" data-testid="vol-tipo"><SelectValue placeholder="Todos / qualquer" /></SelectTrigger>
                      <SelectContent>
                        {servicosUnicos.map(s => (
                          <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Sazonalidade</Label>
                    <Select value={form.sazonalidade} onValueChange={(v) => setForm(f => ({...f, sazonalidade: v}))}>
                      <SelectTrigger className="mt-2 rounded-lg" data-testid="vol-sazo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="media">Média</SelectItem>
                        <SelectItem value="baixa">Baixa</SelectItem>
                        <SelectItem value="sem_sazonalidade">Sem sazonalidade</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Prioridade</Label>
                    <Select value={form.prioridade} onValueChange={(v) => setForm(f => ({...f, prioridade: v}))}>
                      <SelectTrigger className="mt-2 rounded-lg" data-testid="vol-prio"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="alta">Alta</SelectItem>
                        <SelectItem value="media">Média</SelectItem>
                        <SelectItem value="baixa">Baixa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <FF label="Observações" v={form.observacoes} on={(v) => setForm(f => ({...f, observacoes: v}))} testid="vol-obs" />
                <button onClick={submit} className="btn-aj-primary w-full justify-center" data-testid="vol-salvar">Salvar</button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Indicadores operacionais (somente leitura) */}
      {pendentes && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="aj-card p-4" data-testid="vol-pendentes-card">
            <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">
              OS pendentes de distribuição
            </div>
            <div className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1" data-testid="vol-pendentes-count">
              {pendentes.total_pendentes}
            </div>
            <div className="text-[11px] text-aj-text-muted mt-1">
              {Object.entries(pendentes.por_status || {}).map(([st, n]) => `${st.replace(/_/g, " ")}: ${n}`).join(" · ") || "Nenhuma OS aguardando profissional"}
            </div>
          </div>
          <div className="aj-card p-4" data-testid="vol-disponiveis-card">
            <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">
              Montadores disponíveis agora
            </div>
            <div className="font-display font-extrabold text-3xl text-aj-blue-deep mt-1" data-testid="vol-disponiveis-agora-count">
              {pendentes.montadores_disponiveis_agora}
            </div>
            <div className="text-[11px] text-aj-text-muted mt-1">
              Sinalização voluntária de "estou livre agora" — não significa elegibilidade para qualquer OS.
            </div>
          </div>
        </div>
      )}

      {isAdmin && (
        <div>
          <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">Empresa</Label>
          <Select value={empresaSel} onValueChange={setEmpresaSel}>
            <SelectTrigger className="mt-2 max-w-md rounded-lg" data-testid="vol-empresa-sel"><SelectValue placeholder="Selecione empresa" /></SelectTrigger>
            <SelectContent>
              {empresas.map(e => (
                <SelectItem key={e.id} value={e.id}>{e.razao_social || e.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Visão Empresa Selecionada */}
      <div className="aj-card overflow-hidden">
        <div className="border-b border-[#E2E8F0] p-5 flex justify-between items-center bg-aj-surface">
          <div className="flex items-center gap-3">
            <div className="aj-icon-tile"><BarChart3 size={20} /></div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange">
                Volumetria · {isAdmin ? "Empresa selecionada" : "Sua operação"}
              </div>
              <div className="font-display font-extrabold text-2xl text-aj-blue-deep mt-0.5">
                {totalEmpresa} <span className="text-sm font-normal text-aj-text-muted">atend./mês (estimado)</span>
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">Regiões cadastradas</div>
            <div className="font-display font-extrabold text-xl text-aj-blue-deep mt-0.5">{vols.length}</div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-aj-surface-2 text-left">
            <tr><Th>Estado</Th><Th>Cidade</Th><Th>Bairro</Th><Th>Tipo</Th><Th>Sazonalidade</Th><Th>Prioridade</Th><Th>Média/mês</Th><Th></Th></tr>
          </thead>
          <tbody>
            {vols.length === 0 ? (
              <tr><td colSpan={8} className="p-8 text-center text-aj-text-muted">
                {empresaSel ? "Nenhuma região cadastrada. Use \"+ Nova região\" para começar." : "Selecione uma empresa."}
              </td></tr>
            ) : vols.map(v => (
              <tr key={v.id} className="border-t border-[#E2E8F0] hover:bg-aj-surface transition-colors" data-testid={`vol-row-${v.id}`}>
                <Td className="font-mono text-xs">{v.estado}</Td>
                <Td>
                  <span className="inline-flex items-center gap-1.5 font-semibold text-aj-text">
                    <MapPin size={13} className="text-aj-orange" /> {v.cidade}
                  </span>
                </Td>
                <Td>{v.bairro || "—"}</Td>
                <Td className="text-xs">{v.tipo_servico?.replace(/_/g, " ") || "—"}</Td>
                <Td><Tag t={v.sazonalidade} /></Td>
                <Td><Tag t={v.prioridade} hi={v.prioridade === "alta"} /></Td>
                <Td className="font-mono text-right font-bold">{v.media_mensal}</Td>
                <Td>
                  <button onClick={() => remover(v.id)}
                    className="inline-flex items-center gap-1 text-aj-danger text-xs font-semibold hover:underline"
                    data-testid={`vol-del-${v.id}`}>
                    <Trash2 size={12} /> Remover
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Visão Agregada (admin) */}
      {isAdmin && agregado.length > 0 && (
        <div>
          <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-aj-orange mb-3">
            <Globe2 size={13} /> Visão agregada da plataforma (todas as empresas)
          </div>
          <div className="aj-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-aj-surface-2 text-left">
                <tr><Th>Estado</Th><Th>Cidade</Th><Th>Total/mês</Th><Th>Qtd. empresas</Th></tr>
              </thead>
              <tbody>
                {agregado.map((a, i) => (
                  <tr key={i} className="border-t border-[#E2E8F0] hover:bg-aj-surface transition-colors" data-testid={`agg-row-${i}`}>
                    <Td className="font-mono text-xs">{a.estado}</Td>
                    <Td><strong>{a.cidade}</strong></Td>
                    <Td className="font-mono font-bold">{a.media_mensal_total}</Td>
                    <Td>{a.qtd_empresas}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function emptyForm() {
  return {
    estado: "", cidade: "", bairro: "", media_mensal: "",
    tipo_servico: "", sazonalidade: "media", prioridade: "media", observacoes: "",
  };
}
function Tag({ t, hi }) {
  if (!t) return <span className="text-aj-text-muted">—</span>;
  return (
    <span className={`px-2 py-0.5 rounded-md text-[10px] font-semibold capitalize ${
      hi ? "bg-aj-danger text-white" : "bg-aj-surface-2 text-aj-text"
    }`}>{t.replace("_", " ")}</span>
  );
}
function FF({ label, v, on, testid, type="text" }) {
  return (
    <div>
      <Label className="text-[11px] font-bold uppercase tracking-wider text-aj-text-muted">{label}</Label>
      <Input type={type} value={v || ""} onChange={(e) => on(e.target.value)} className="mt-2 rounded-lg" data-testid={testid} />
    </div>
  );
}
function Th({ children }) { return <th className="p-3 text-[10px] font-bold uppercase tracking-wider text-aj-text-muted">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
